import React, { useEffect, useMemo, useState } from "react";
import getBooking from "../../../../controllers/booking/getBooking";
import { FareRulesModal } from "../../../../components/modal/FareRulesModal.jsx";
import getFlightOfferFarerule from "../../../../controllers/Flight/getOfferFarerule";
import {
  getFlightBookingFromChangeData,
  getRawFareRulesText,
  hasFormattedFareRules,
} from "../../../../data/flight/order/getChangeFareRuleInsight";

/**
 * Opens immediately, then loads fare rules:
 * 1) use changeOrder.flightBooking.formattedFareRules if present
 * 2) else GET book/:flightBooking.bookingId for raw fareRules, then POST format
 */
export default function ChangeFareRulesModal({ data, open, setOpen }) {
  const flightBooking = useMemo(
    () => getFlightBookingFromChangeData(data),
    [data],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formattedFareRules, setFormattedFareRules] = useState(null);
  const [rawFareRules, setRawFareRules] = useState("");

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    async function run() {
      setError("");
      setLoading(true);
      setFormattedFareRules(null);
      setRawFareRules("");

      try {
        if (hasFormattedFareRules(flightBooking?.formattedFareRules)) {
          if (!cancelled) {
            setFormattedFareRules(flightBooking.formattedFareRules);
          }
          return;
        }

        const bookingId = flightBooking?.bookingId;
        if (!bookingId) {
          if (!cancelled) {
            setError(
              "No booking id on this change request. Fare rules cannot be loaded.",
            );
          }
          return;
        }

        const bookingRes = await getBooking(bookingId);
        if (cancelled) return;
        if (!bookingRes?.return) {
          setError(
            bookingRes?.msg ||
              "Could not load the booking to read fare rules.",
          );
          return;
        }

        const raw =
          getRawFareRulesText(
            data?.fareRules,
            bookingRes.data?.fareRules,
            flightBooking?.fareRules,
          ) || "";

        if (!raw) {
          setError("This booking has no raw fareRules text to format.");
          return;
        }

        setRawFareRules(raw);

        if (!flightBooking?._id) {
          setError("Missing flight booking id. Fare rules cannot be formatted.");
          return;
        }

        const res = await getFlightOfferFarerule({
          flightBookingId: flightBooking._id,
          segments: (flightBooking?.flights || []).map((flight) => ({
            segmentRef: flight?.segmentRef,
            departureLocation: flight?.departureLocation,
            departureLocationName: flight?.departureLocationName,
            arrivalLocation: flight?.arrivalLocation,
            arrivalLocationName: flight?.arrivalLocationName,
          })),
          fareRules: raw,
        });

        if (cancelled) return;

        if (res?.return) {
          setFormattedFareRules(res.data);
          return;
        }

        setError(
          res?.msg ||
            "Fare rules could not be read. That is not a refusal — you can still send the change.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    data?.fareRules,
    flightBooking?._id,
    flightBooking?.bookingId,
    flightBooking?.fareRules,
    flightBooking?.formattedFareRules,
    flightBooking?.flights,
  ]);

  return (
    <FareRulesModal
      open={open}
      setOpen={setOpen}
      fareRules={rawFareRules}
      formattedFareRules={formattedFareRules}
      loading={loading}
      error={error}
    />
  );
}
